import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { VxAPI } from '../../api/vx.api';
import { CustomerObjectDTO } from '../dto/customerObject.dto';
import Auth from '../../utils/auth';
import { QiEmployeeProps } from '../dto/QiEmployeeProps.dto';
import { UseCasesObjectDTO } from '../dto/useCasesObject.dto';
import './editModuleForm.scss';
import { ModuleObjectDTO } from '../dto/moduleObject.dto';
import { CourseObjectDTO } from '../dto/courseObject.dto';
import { TagObjectDTO } from '../dto/tagObject.dto';

const EditModuleForm: React.FC<QiEmployeeProps> = (props) => {
  const { userCustomerId } = props;
  const params = useParams();
  const paramsModuleId = Number(params.moduleId);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loggedInUserCustomerId, setLoggedInUserCustomerId] = useState<number | null | undefined>(null);

  const [moduleInfo, setModuleInfo] = useState<ModuleObjectDTO>({
    title: '',
    description: '',
    notes: '',
    type: '',
    version: 0,
    courseId: 0,
    tags: [],
    courseInfo: undefined,
  });
  const [customerArray, setCustomerArray] = useState<CustomerObjectDTO[]>([
    {
      customerId: 0,
      lastName: '',
      firstName: '',
      phone: '',
      email: '',
      location: '',
    },
  ]);
  const [useCaseArray, setUseCaseArray] = useState<UseCasesObjectDTO[]>([
    {
      useCaseId: 0,
      title: '',
      description: '',
      notes: '',
    },
  ]);

  const [courseObjectArrayData, setCourseObjectArrayData] = useState<CourseObjectDTO[]>();
  const [moduleUseCaseId, setModuleUseCaseId] = useState<string | number | undefined>();
  const [modulesObjectWithUseCaseNames, setModulesObjectWithUseCaseNames] = useState<ModuleObjectDTO>();
  const [tagArray, setTagArray] = useState<TagObjectDTO[]>();
  const [availableTags, setAvailableTags] = useState<TagObjectDTO[]>();
  const [selectTagState, setSelectTagState] = useState<number[]>([]);
  const [currentlySelectedTagIds, setCurrentlySelectedTagIds] = useState<number[]>([]);
  const { title, description, notes, version, type, courseInfo } = moduleInfo;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMessage('');
    setModuleInfo({ ...moduleInfo, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newToken = await Auth.refreshToken();

    let versionArray = version.toString().split('.');
    let periodCount = versionArray.length;
    let versionNumber = Number(version);

    if (periodCount > 1 || Number.isInteger(versionNumber) !== true || versionNumber <= 0) {
      setMessage('Please enter a version number in valid form as a whole number: "1", "2", etc.');
      return;
    }

    if (title === '' || description === '' || version === 0) {
      setMessage('Make sure all required fields are completed.');
      return;
    }
    let tags: number[] = selectTagState;
    if (selectTagState?.length > 0) {
      selectTagState.forEach((tagId) => {
        const foundTag = tagArray?.find((tag) => tag.tagId === tagId);
        const tagType = tagArray?.find((tag) => tag.title === foundTag?.type)?.tagId;
        const tagSub1 = tagArray?.find((tag) => tag.title === foundTag?.sub1)?.tagId;
        const tagSub2 = tagArray?.find((tag) => tag.title === foundTag?.sub2)?.tagId;
        if (tagType && !tags.includes(tagType)) {
          tags.push(tagType);
        }
        if (tagSub1 && !tags.includes(tagSub1)) {
          tags.push(tagSub1);
        }
        if (tagSub2 && !tags.includes(tagSub2)) {
          tags.push(tagSub2);
        }
      });
    }
    let currentTime = Date.now(); // get date as integer
    let currentDate = new Date(currentTime); // conver integer into date with timezone
    let modifiedDate = currentDate;

    setMessage('Editing...');
    const resp = await VxAPI.editModule(
      {
        title,
        description,
        notes,
        version,
        type,
        tags,
        modifiedDate,
      },
      paramsModuleId,
    );

    if (resp.moduleId) {
      window.location.assign('/modules');
    } else {
      setMessage('An error has occured. Please try again. If error persists contact your administrator.');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const moduleData = await VxAPI.getSingleModuleData(paramsModuleId);

      if (moduleData) {
        setModuleInfo(moduleData);
        if (moduleData.tags && moduleData.tags[0] !== 0) {
          setSelectTagState(moduleData.tags);
          setCurrentlySelectedTagIds(moduleData.tags);
        }
      }

      const customerData = await VxAPI.getAllCustomersData();

      if (customerData) {
        setCustomerArray(customerData);
      }

      const useCaseData = await VxAPI.getAllUseCasesData();

      if (useCaseData) {
        setUseCaseArray(useCaseData);
      }

      const coursesData = await VxAPI.getAllCoursesData();

      if (coursesData) {
        setCourseObjectArrayData(coursesData);
      }

      const tagsData = await VxAPI.getAllTagsData();

      if (tagsData) {
        setTagArray(tagsData);
        setAvailableTags(tagsData);
      }
    };

    fetchData().catch(console.error);
  }, []);

  useEffect(() => {
    if (
      useCaseArray[0].useCaseId !== 0 &&
      customerArray[0].customerId !== 0 &&
      courseObjectArrayData &&
      moduleInfo.courseId !== 0
    ) {
      let foundCourse = courseObjectArrayData?.find((course) => course.courseId === moduleInfo.courseId);
      let courseName = courseObjectArrayData?.find((course) => course.courseId === moduleInfo.courseId)?.title;
      let courseUseCaseId = courseObjectArrayData?.find((course) => course.courseId === moduleInfo.courseId)?.useCaseId;
      let useCaseName = useCaseArray.find((useCase) => useCase.useCaseId === courseUseCaseId)?.title;
      let customerFirstName = customerArray.find(
        (customer) => customer.customerId === foundCourse?.customerId,
      )?.firstName;
      let customerLastName = customerArray.find(
        (customer) => customer.customerId === foundCourse?.customerId,
      )?.lastName;
      moduleInfo.courseTitle = courseName;
      moduleInfo.useCaseTitle = useCaseName;
      moduleInfo.customerFirstName = customerFirstName;
      moduleInfo.customerLastName = customerLastName;
      setModuleUseCaseId(courseUseCaseId);
      setModulesObjectWithUseCaseNames(moduleInfo);
      setLoading(false);
    }
  }, [moduleInfo, useCaseArray, courseObjectArrayData, customerArray]);

  const handleTagSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setMessage('');
    const selectedOptions = e.currentTarget.selectedOptions;
    const newTags = [];

    for (let i = 0; i < selectedOptions.length; i++) {
      newTags.push(Number(selectedOptions[i].value));
    }

    setSelectTagState(newTags);
  };

  const handleTagReset = () => {
    setSelectTagState(currentlySelectedTagIds);
  };

  return (
    <>
      {loading ? (
        <div>
          <h1 className="loading">Loading...</h1>
        </div>
      ) : (
        <div id="edit-module-div">
          <h1>Edit Module</h1>
          <form onSubmit={handleSubmit}>
            <div className="dropdown-div">
              <div className="edit-module-hardcode">
                <label htmlFor="name">Customer</label>
                <p id="module-cust-name">
                  {modulesObjectWithUseCaseNames?.customerFirstName} {modulesObjectWithUseCaseNames?.customerLastName}
                </p>
              </div>
              <div className="edit-module-hardcode">
                <label htmlFor="useCaseId">Use Case</label>
                <p id="module-cust-useCase">{modulesObjectWithUseCaseNames?.useCaseTitle}</p>
              </div>
            </div>
            <div className="edit-module-row">
              <div className="edit-module-form-input">
                <label htmlFor="title">Title *</label>
                <input type="text" name="title" defaultValue={title} onChange={handleChange} />
              </div>
              <div className="edit-module-hardcode">
                <label htmlFor="Course Name">Course Name</label>
                <p id="module-cust-course">{modulesObjectWithUseCaseNames?.courseTitle}</p>
              </div>
            </div>
            <div className="edit-module-row">
              <div className="edit-module-form-input">
                <label htmlFor="version">Version *</label>
                <input type="text" name="version" defaultValue={version} onChange={handleChange} />
              </div>
            </div>
            <div className="edit-module-row">
              <div className="edit-tag-dropdown">
                <label id="tag-dropdown-header" htmlFor="selectTagState[]">
                  Edit Tags
                </label>
                <h2 id="tag-select-header">Hold CTRL or CMD to select multiple</h2>
                <select multiple size={availableTags?.length} name="tagArray" onChange={handleTagSelection}>
                  {availableTags
                    ? availableTags
                    .sort((a: any, b: any) => {
                      if (a.title < b.title) {
                        return -1;
                      }
                      if (a.title > b.title) {
                        return 1;
                      }
                      return 0;
                    })
                    .map((tag, index: number) => (
                        <option
                          {...(selectTagState.includes(Number(tag.tagId)) ? { selected: true } : null)}
                          key={index}
                          value={tag.tagId}
                        >
                          {tag.title}
                        </option>
                      ))
                    : null}
                </select>
                <button type="button" id="reset-btn" onClick={handleTagReset}>
                  <span>Reset</span>
                </button>
              </div>
            </div>
            <div className="edit-module-form-textarea">
              <label htmlFor="description">Description *</label>
              <textarea name="description" defaultValue={description} onChange={handleChange} />
            </div>
            <div className="edit-module-form-textarea">
              <label htmlFor="notes">Notes</label>
              <textarea name="notes" defaultValue={notes} onChange={handleChange} />
            </div>
            <div id="edit-module-btns">
              <button type="button" onClick={() => window.location.assign('/modules')}>
                <span>Cancel</span>
              </button>
              <button type="submit">
                <span>Confirm</span>
              </button>
            </div>
          </form>
          {message ? <h3 className="message">{message}</h3> : null}
        </div>
      )}
    </>
  );
};

export default EditModuleForm;
